<script setup lang="ts">
/**
 * Loading placeholder for the sidebar's registration list
 * (see nav/RegsAndSleevesList.vue, which this deliberately mirrors).
 *
 * Shown while useSidebarData is fetching registrations and sleeves. Before
 * this existed the sidebar rendered nothing at all during that wait, so the
 * whole left column sat empty and then popped in fully formed — the one part
 * of the shell with no loading affordance.
 *
 * SHAPE: each placeholder card repeats the real row's structure exactly —
 * the same `border-black/10 border-2 rounded my-3` wrapper, a bold name line,
 * an account-number line paired with a right-aligned value, and the
 * chevron + "Show N Sleeves" footer. Matching the silhouette (and the
 * paddings that produce it) is what stops the sidebar from reflowing when
 * the real rows replace these.
 *
 * CONTRAST: the sidebar sits on a warm gradient (#E0E0D2 → #7A7A73), so the
 * bars use `azzad-shimmer-veil` — a translucent darkening — rather than
 * either opaque tint. Both opaque tints cross the gradient somewhere down the
 * column and become invisible at that point (the strong one measures 1.00:1
 * contrast about a quarter of the way down). A veil is always darker than
 * whatever is behind it. Hierarchy is carried by bar width and height instead
 * of by tint. See the loading & motion block in assets/css/main.css.
 */
withDefaults(defineProps<{
  /** How many placeholder cards to draw. Six fills a typical viewport
   *  without implying a specific number of accounts. */
  count?: number
}>(), {
  count: 6
})

// Deterministic width jitter so the placeholder reads like a list of real,
// differently-named accounts rather than a stack of identical bars — and so
// it stays put across re-renders instead of reshuffling.
const nameWidth = (i: number) => [88, 72, 95, 66, 82, 78][i % 6]
const valueWidth = (i: number) => [58, 46, 64, 52, 60, 48][i % 6]
</script>

<template>
  <div aria-busy="true" aria-live="polite" aria-label="Loading accounts">
    <div
      v-for="i in count"
      :key="i"
      class="border-black/10 border-2 rounded my-3 w-full pl-0.5"
      :style="{ '--azzad-wave': i }"
    >
      <div class="py-2">
        <!-- Registration name -->
        <div class="flex items-center justify-between pr-4">
          <span
            class="azzad-shimmer azzad-shimmer-veil ml-3 h-3 rounded"
            :style="{ width: `${nameWidth(i - 1)}%` }"
          />
        </div>

        <!-- Account number + market value -->
        <div class="flex items-center justify-between pr-4 mt-2 gap-3">
          <span class="azzad-shimmer azzad-shimmer-veil ml-3 h-2.5 w-16 rounded shrink-0" />
          <span
            class="azzad-shimmer azzad-shimmer-veil h-3 rounded"
            :style="{ width: `${valueWidth(i - 1)}px` }"
          />
        </div>
      </div>

      <!-- Chevron + "Show N Sleeves" -->
      <div class="flex items-center pl-3 py-1 gap-2">
        <span class="azzad-shimmer azzad-shimmer-veil size-4 rounded-full shrink-0" />
        <span class="azzad-shimmer azzad-shimmer-veil h-2 w-20 rounded" />
      </div>
    </div>
  </div>
</template>
